<template>
  <div class="container-fluid">
    <page-header :page="page">
      <template v-slot:customActions>
        <ActionButton
          variant="outline-success m-1"
          text="Panel de Uso"
          i="fas fa-chart-line"
          :responsive="false"
          @click="goToUsageDashboard"
        />
        <ActionButton
          v-if="hasSearched"
          variant="success m-1"
          text="Crear nueva versión"
          i="fas fa-file-plus"
          :responsive="false"
          @click="goToCreate(published)"
        />
      </template>
    </page-header>

    <filter-manager
      ref="filter"
      v-model="filters"
      :display-advanced="false"
      items-container-class="flex-wrap"
      actions-container-class="col-sm-3 ml-0 mr-0 text-right"
      :filters="filtersHeader"
      search-text="Buscar"
      @search="onSearch"
    />

    <div v-if="hasSearched">
      <CustomSeparator text="Versión Publicada" class="mt-4 custom-separator-line" />

      <CustomResultNotFound
        v-if="!published"
        class="mt-3"
        text="No hay ninguna versión Publicada para esta clave todavía."
      />

      <div v-else class="published-card mt-3">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <span class="version-badge">v{{ published.version }}</span>
            <span class="status-badge status-publicado">Publicado</span>
            <span class="curriculo-tag" v-if="published.referenceCurriculum">
              {{ published.referenceCurriculum }}
            </span>
          </div>
          <!--
            26 ago 2026: antes decía "Editar (nueva versión)" y llamaba goToCreate(published) --
            pero eso es exactamente lo mismo que ya hace el botón "Crear nueva versión" del
            encabezado de arriba (mismo @click). Astianax señaló que desde la fila Publicada la
            opción de esta tarjeta debería ser "Ver" (abrir el detalle completo de solo lectura,
            igual que el "Ver" de cualquier fila en el historial de abajo), no otro atajo
            duplicado para crear una versión nueva -- ese ya existe arriba.
          -->
          <ActionButton
            text="Ver"
            variant="outline-success"
            i="fas fa-eye"
            :responsive="false"
            @click="viewVersion(published)"
          />
        </div>

        <p class="field-label mt-3">System Message</p>
        <p class="field-value">{{ published.systemMessageText }}</p>

        <p class="field-label mt-2">Prompt Text</p>
        <p class="field-value">{{ published.promptText }}</p>

        <p class="field-label mt-2">Fuentes de contexto</p>
        <p class="field-value">
          <span v-if="!published.sources || published.sources.length === 0">
            Ninguna fuente marcada.
          </span>
          <span v-else class="fuente-chip" v-for="f in published.sources" :key="f">{{ f }}</span>
        </p>
      </div>

      <CustomSeparator text="Historial de versiones" class="mt-4 custom-separator-line" />

      <CustomResultNotFound
        v-if="history.length === 0"
        class="mt-3"
        text="No hay historial todavía para esta clave."
      />

      <div v-else class="mt-3">
        <HistoryRow
          v-for="item in history"
          :key="item.aiPromptConfigurationId"
          :item="item"
          @view="viewVersion"
          @create-version="goToCreate"
          @publish="onPublish"
          @archive="onArchive"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import HistoryRow from './components/HistoryRow';
import alertMixin from '@/mixin/alert.mixin';
import {
  getPublished,
  getHistory,
  publishConfiguration,
  archiveConfiguration,
  getModulosCatalogo,
  getSeccionesCatalogo,
} from '@/modules/operation-ai-prompt-management/api';

const CREATE_ROUTE_NAME = 'aiPromptConfigCreateEdit';
const USAGE_ROUTE_NAME = 'aiPromptConfigUsage';

export default {
  name: 'AiPromptConfigMain',
  mixins: [alertMixin],
  components: {
    PageHeader,
    ActionButton,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    HistoryRow,
  },
  data() {
    return {
      page: {
        icon: 'fas fa-robot',
        title: 'Consola de Instrucciones IA',
        routes: ['Inicio', 'Operaciones', 'Consola de Instrucciones IA'],
      },
      filters: {
        module: null,
        sectionArea: null,
      },
      modulosCatalogo: [],
      seccionesCatalogo: [],
      hasSearched: false,
      published: null,
      history: [],
    };
  },
  computed: {
    // Igual que en CreateEditForm.vue: el catálogo real (CODE_AIPromptModule/
    // CODE_AIPromptSection) solo alimenta las opciones del filtro -- lo que se envía a
    // getPublished/getHistory sigue siendo el NAME (string), no el Id.
    modulosOptions() {
      return this.modulosCatalogo.map((m) => ({ value: m.name, text: m.name }));
    },
    seccionesOptions() {
      const moduloSeleccionado = this.modulosCatalogo.find(
        (m) => m.name === (this.filters.module && this.filters.module.value)
      );
      const filtradas = moduloSeleccionado
        ? this.seccionesCatalogo.filter(
            (s) => s.aiPromptModuleId === moduloSeleccionado.aiPromptModuleId
          )
        : this.seccionesCatalogo;
      return filtradas.map((s) => ({ value: s.name, text: s.name }));
    },
    filtersHeader() {
      return [
        {
          name: 'module',
          component: 'CustomDropDown',
          options: this.modulosOptions,
          value: this.filters.module,
          fieldKey: 'value',
          fieldName: 'text',
          placeholder: 'Módulo',
          class: 'col-sm-6 col-md-4 col-lg-3 ml-0 mr-0',
        },
        {
          name: 'sectionArea',
          component: 'CustomDropDown',
          options: this.seccionesOptions,
          value: this.filters.sectionArea,
          fieldKey: 'value',
          fieldName: 'text',
          placeholder: 'Sección / Área',
          class: 'col-sm-6 col-md-4 col-lg-4 ml-0 mr-0',
        },
      ];
    },
  },
  methods: {
    async loadCatalogos() {
      const [modulos, secciones] = await Promise.all([
        getModulosCatalogo(),
        getSeccionesCatalogo(),
      ]);
      this.modulosCatalogo = modulos;
      this.seccionesCatalogo = secciones;
      // Preselecciona el primer Módulo del catálogo real, igual que antes hacía
      // MODULOS_AI_PROMPT[0] con el valor hardcodeado.
      if (!this.filters.module && this.modulosOptions.length > 0) {
        this.filters.module = this.modulosOptions[0];
      }
    },
    async onSearch() {
      if (!this.filters.module || !this.filters.sectionArea) {
        this.ShowToast('Alerta', 'Seleccione Módulo y Sección / Área.', 'error');
        return;
      }

      const moduleName = this.filters.module.value;
      const sectionArea = this.filters.sectionArea.value;

      try {
        this.published = await getPublished(moduleName, sectionArea);
        this.history = await getHistory(moduleName, sectionArea);
        this.hasSearched = true;
      } catch (e) {
        this.ShowToast('Error', e.message, 'error');
      }
    },
    // Navegación explícita hacia el panel de uso/estadísticas ("PEI GPT — Panel de Uso") desde
    // la Consola de Instrucciones IA -- pedido explícito: la nueva pantalla debe poder alcanzarse
    // desde aquí, no solo por URL directa.
    goToUsageDashboard() {
      this.$router.push({ name: USAGE_ROUTE_NAME });
    },
    goToCreate(sourceConfig) {
      this.$router.push({
        name: CREATE_ROUTE_NAME,
        query: {
          module: this.filters.module.value,
          sectionArea: this.filters.sectionArea.value,
          sourceId: sourceConfig ? sourceConfig.aiPromptConfigurationId : undefined,
        },
      });
    },
    // "Ver" es de solo lectura -- a diferencia de goToCreate, agrega mode=view a la ruta.
    // CreateEdit.vue lee ese query param para deshabilitar todos los campos y ocultar los
    // botones de Guardar/Publicar (antes "Ver" abría el mismo formulario editable que "Editar
    // (nueva versión)", lo cual confundía: ver una versión Publicada mostraba "Guardar y
    // Publicar" como si se fuera a modificar esa misma fila).
    goToView(item) {
      this.$router.push({
        name: CREATE_ROUTE_NAME,
        query: {
          module: this.filters.module.value,
          sectionArea: this.filters.sectionArea.value,
          sourceId: item.aiPromptConfigurationId,
          mode: 'view',
        },
      });
    },
    viewVersion(item) {
      this.goToView(item);
    },
    async onPublish(item) {
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Publicar la versión ${item.version}? Esto archivará automáticamente la versión Publicada actual, si existe.`
      );
      if (!answer) {
        return;
      }
      try {
        await publishConfiguration(item.aiPromptConfigurationId);
        this.ShowToast('Proceso Exitoso.', 'La versión fue publicada.', 'success');
        await this.onSearch();
      } catch (e) {
        this.ShowToast('Error', e.message, 'error');
      }
    },
    async onArchive(item) {
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Archivar la versión ${item.version}?`
      );
      if (!answer) {
        return;
      }
      try {
        await archiveConfiguration(item.aiPromptConfigurationId);
        this.ShowToast('Proceso Exitoso.', 'La versión fue archivada.', 'success');
        await this.onSearch();
      } catch (e) {
        this.ShowToast('Error', e.message, 'error');
      }
    },
  },
  created() {
    this.loadCatalogos().catch((e) => {
      this.ShowToast('Error', 'No se pudo cargar el catálogo de Módulos/Secciones. ' + e.message, 'error');
    });
  },
};
</script>

<style scoped>
.published-card {
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  box-shadow: 0px 3px 6px #00000014;
  padding: 16px 20px;
}

.version-badge {
  font: bold 12px/1 Lato;
  color: #11291b;
  background: #f2f2f2;
  border-radius: 20px;
  padding: 4px 10px;
  margin-right: 8px;
}

.status-badge {
  font: bold 11px/1 Lato;
  border-radius: 20px;
  padding: 4px 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.status-publicado {
  background: #e3f6ee;
  color: #007a50;
}

.curriculo-tag {
  font-size: 12px;
  color: #6b6b6b;
  margin-left: 8px;
}

.field-label {
  font: bold 12px/1 Lato;
  color: #2db782;
  margin-bottom: 4px;
}

.field-value {
  font-size: 13.5px;
  color: #11291b;
  white-space: pre-wrap;
}

.fuente-chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  color: #007a50;
  border: 1px solid #b7e4d3;
  background: #e3f6ee;
  border-radius: 20px;
  padding: 2px 10px;
  margin: 0 6px 6px 0;
}
</style>
